import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {getCycles, createCycle, updateCycle, updatePond, fetchPond, createFeedRecord, getFeedRecords} from "../../api/pond";
import {fetchTemplateNames, fetchTemplateByName} from "../../api/feed";
import {useForm} from "../../utils/useForm";
import {FORM_VALIDATION_CONFIGS} from "../../utils/formValidation";
import FormField from "../../components/common/FormField";
import {useTranslation} from "react-i18next";

interface CycleTabProps {
  pondId: string;
}

const CycleTab: React.FC<CycleTabProps> = ({pondId}) => {
  const [cycles, setCycles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCycle, setEditingCycle] = useState<any>(null);
  const [templateNames, setTemplateNames] = useState<{templateName: string}[]>([]);
  const [pondDetails, setPondDetails] = useState<any>(null);
  const {t} = useTranslation();

  const form = useForm({
    initialValues: {
      startedAt: "",
      finishedAt: "",
      totalSeed: 0,
      seedType: "hatchery" as "hatchery" | "wild" | "broodstock" | "other",
      feedType: "",
      notes: "",
    },
    validationConfig: FORM_VALIDATION_CONFIGS.CYCLE,
    onSubmit: async (values) => {
      // Ensure finishedAt is null if blank
      const payload = {
        ...values,
        finishedAt: values.finishedAt ? values.finishedAt : null,
      };

      // Remove feedType from cycle payload since it's not part of PondCycle
      const {feedType, ...cyclePayload} = payload;

      try {
        if (editingCycle) {
          await updateCycle(editingCycle.id, cyclePayload);
        } else {
          // Create new cycle
          await createCycle(pondId, cyclePayload);

          // Auto-populate feed records for the first 30 days if feedType is provided
          if (feedType && values.totalSeed > 0 && values.startedAt) {
            console.log("Auto-populating feed records for new cycle");
            await autoPopulateFeedRecords(feedType, values.totalSeed, values.startedAt);
          }
        }

        // If feedType is provided, update the pond's feedType
        if (feedType) {
          await updatePond(pondId, {feedType});
        }

        handleCloseModal();
        fetchCycles();
      } catch (err) {
        setError("Failed to save cycle");
      }
    },
  });

  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg")); // lg = 1200px

  useEffect(() => {
    fetchCycles();
    loadTemplateNames();
    loadPondDetails();
  }, [pondId]);

  const loadTemplateNames = async () => {
    try {
      const data = await fetchTemplateNames();
      setTemplateNames(data);
    } catch (err) {
      console.error("Failed to load template names:", err);
    }
  };

  const loadPondDetails = async () => {
    try {
      const data = await fetchPond(pondId);
      setPondDetails(data);
    } catch (err) {
      console.error("Failed to load pond details:", err);
    }
  };

  const autoPopulateFeedRecords = async (feedType: string, totalSeed: number, startDate: string) => {
    try {
      console.log(`Auto-populating feed records for feed type: ${feedType}, total seed: ${totalSeed}`);

      // Fetch template data by template name
      const template = await fetchTemplateByName(feedType);
      console.log("Template data:", template);

      if (!template.templateData || template.templateData.length === 0) {
        console.warn("No template data found for auto-population");
        return;
      }

      // Get existing feed records to find gaps
      const existingFeedRecords = await getFeedRecords(pondId);
      const existingDates = new Set(existingFeedRecords.map((record: any) => record.feedDate));

      console.log("Existing feed record dates:", Array.from(existingDates));

      // Calculate how many days have passed since the cycle started
      const cycleStartDate = new Date(startDate);
      const today = new Date();
      const daysSinceStart = Math.floor((today.getTime() - cycleStartDate.getTime()) / (1000 * 60 * 60 * 24));

      console.log(`Cycle started on: ${startDate}, Today: ${today.toISOString().split("T")[0]}, Days since start: ${daysSinceStart}`);

      // Find the range of days we need to check (from 0 to max template day or current day, whichever is larger)
      const maxTemplateDay = Math.max(...template.templateData.map((data) => data.doc));
      const maxDayToCheck = Math.max(maxTemplateDay, daysSinceStart);

      console.log(`Template has data for days 0-${maxTemplateDay}, cycle is ${daysSinceStart} days old, checking up to day ${maxDayToCheck}`);

      // Find gaps in feed records from day 0 to max day
      const missingDays: Array<{day: number; date: string}> = [];
      for (let day = 0; day <= maxDayToCheck; day++) {
        const feedDate = new Date(cycleStartDate);
        feedDate.setDate(feedDate.getDate() + day);
        const dateString = feedDate.toISOString().split("T")[0];

        if (!existingDates.has(dateString)) {
          missingDays.push({day, date: dateString});
        }
      }

      console.log(`Found ${missingDays.length} missing days:`, missingDays);

      // Filter template data for missing days only, but only if template has data for that day
      const relevantTemplateData = template.templateData
        .filter((data) => missingDays.some((missing) => missing.day === data.doc))
        .sort((a, b) => a.doc - b.doc);

      console.log(`Creating feed records for missing days:`, relevantTemplateData);

      // Calculate scaling factor based on template's stock count
      // Find the template's base stock count from the first data entry (usually day 0 or 1)
      const firstTemplateData = relevantTemplateData[0];
      const templateStackSize = firstTemplateData?.count || 100000; // Default to 100,000 if not available
      const scalingFactor = totalSeed / templateStackSize;

      console.log(`Template stack size: ${templateStackSize}, Pond stack size: ${totalSeed}`);
      console.log(`Scaling factor: ${scalingFactor} (${totalSeed} / ${templateStackSize})`);

      // Create feed records for each day
      for (const templateData of relevantTemplateData) {
        // Calculate the actual DOC (Days of Culture) - should start from 0
        // If template starts from DOC 1, we need to map it to start from DOC 0
        const actualDOC = templateData.doc - 1; // Adjust to start from 0

        // Calculate the feed date based on the cycle start date
        const feedDate = new Date(startDate);
        feedDate.setDate(feedDate.getDate() + actualDOC);

        // Scale feed values based on actual pond stack size
        const scaledFeed1 = Math.round((templateData.feed1 || 0) * scalingFactor);
        const scaledFeed2 = Math.round((templateData.feed2 || 0) * scalingFactor);
        const scaledFeed3 = Math.round((templateData.feed3 || 0) * scalingFactor);
        const scaledFeed4 = Math.round((templateData.feed4 || 0) * scalingFactor);

        const feedRecord = {
          feedDate: feedDate.toISOString().split("T")[0],
          feed1: scaledFeed1,
          feed2: scaledFeed2,
          feed3: scaledFeed3,
          feed4: scaledFeed4,
          remark: "Auto-populated",
          feedType: feedType,
        };

        console.log(`Creating feed record for DOC ${actualDOC}:`, feedRecord);
        await createFeedRecord(pondId, feedRecord);
      }

      console.log(`Successfully auto-populated ${relevantTemplateData.length} feed records with scaling factor ${scalingFactor}`);
    } catch (error) {
      console.error("Error auto-populating feed records:", error);
      // Don't throw error to avoid breaking cycle creation
      // Just log it for debugging
    }
  };

  const fetchCycles = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCycles(pondId);
      setCycles(data.data);
    } catch (err) {
      setError("Failed to load cycles");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (cycle?: any) => {
    if (cycle) {
      setEditingCycle(cycle);
      form.setValues({
        startedAt: cycle.startedAt ? new Date(cycle.startedAt).toISOString().split("T")[0] : "",
        finishedAt: cycle.finishedAt ? new Date(cycle.finishedAt).toISOString().split("T")[0] : "",
        totalSeed: cycle.totalSeed,
        seedType: cycle.seedType,
        feedType: pondDetails?.feedType || "", // Use pond's feedType when editing
        notes: cycle.notes || "",
      });
    } else {
      setEditingCycle(null);
      form.setValues({
        startedAt: "",
        finishedAt: "",
        totalSeed: 0,
        seedType: "hatchery" as "hatchery" | "wild" | "broodstock" | "other",
        feedType: pondDetails?.feedType || "", // Pre-select pond's current feedType for new cycles
        notes: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCycle(null);
    form.reset();
  };

  const handleSave = () => {
    form.handleSubmit();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{xs: "column", md: "column", lg: "row"}}
        alignItems={{xs: "flex-start", md: "flex-start", lg: "center"}}
        justifyContent="space-between"
        mb={1}
        gap={0.5}>
        <Typography variant="subtitle1">{t("Cycles")}</Typography>
        <Button variant="contained" color="primary" size="small" sx={{mt: {xs: 0.5, md: 0.5, lg: 0}}} onClick={() => handleOpenModal()}>
          {t("Add Cycle")}
        </Button>
      </Box>
      {isCompact ? (
        <Box display="flex" flexDirection="column" gap={1}>
          {cycles.map((cycle) => (
            <Paper key={cycle.id} sx={{p: {xs: 1, md: 1, lg: 0.5}, fontSize: {xs: "0.95rem", md: "0.9rem", lg: "0.85rem"}}}>
              <Box display="flex" flexWrap="wrap" gap={1}>
                <Box minWidth={120}>
                  <b>DOC:</b> {cycle.doc || 0}
                </Box>
                <Box minWidth={120}>
                  <b>{t("Started At")}:</b> {cycle.startedAt ? new Date(cycle.startedAt).toLocaleDateString() : "-"}
                </Box>
                <Box minWidth={120}>
                  <b>{t("Finished At")}:</b> {cycle.finishedAt ? new Date(cycle.finishedAt).toLocaleDateString() : "-"}
                </Box>
                <Box minWidth={120}>
                  <b>{t("Total Seed")}:</b> {cycle.totalSeed}
                </Box>
                <Box minWidth={120}>
                  <b>{t("Seed Type")}:</b> {cycle.seedType}
                </Box>
                <Box minWidth={120}>
                  <b>{t("Feed Type")}:</b> {pondDetails?.feedType || "-"}
                </Box>
                <Box minWidth={120}>
                  <b>FCR:</b> {cycle.fcr ?? "-"}
                </Box>
                <Box minWidth={120}>
                  <b>ADG:</b> {cycle.adg ?? "-"}
                </Box>
                <Box minWidth={120}>
                  <b>SR:</b> {cycle.sr ?? "-"}
                </Box>
                <Box minWidth={120}>
                  <b>ABW:</b> {cycle.abw ?? "-"}
                </Box>
                <Box minWidth={120}>
                  <b>{t("Harvest")}:</b> {cycle.harvestWeight ?? "-"}
                </Box>
                <Box minWidth={120}>
                  <b>{t("Notes")}:</b> {cycle.notes ?? ""}
                </Box>
                <Box minWidth={120}>
                  <Button size="small" onClick={() => handleOpenModal(cycle)}>
                    {t("Edit")}
                  </Button>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Box width="100%" position="relative" sx={{overflowX: "auto"}}>
          <TableContainer component={Paper} sx={{width: "100%", minWidth: 500, overflowX: "auto"}}>
            <Table size="small" sx={{minWidth: 500}}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">DOC</TableCell>
                  <TableCell align="center">{t("Start Date")}</TableCell>
                  <TableCell align="center">{t("Finished At")}</TableCell>
                  <TableCell align="center">{t("Total Seed")}</TableCell>
                  <TableCell align="center">{t("Seed Type")}</TableCell>
                  <TableCell align="center">{t("Feed Type")}</TableCell>
                  <TableCell align="center">FCR</TableCell>
                  <TableCell align="center">ADG</TableCell>
                  <TableCell align="center">SR</TableCell>
                  <TableCell align="center">ABW</TableCell>
                  <TableCell align="center">{t("Harvest")}</TableCell>
                  <TableCell align="center">{t("Notes")}</TableCell>
                  <TableCell align="center">{t("Actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cycles.map((cycle) => (
                  <TableRow key={cycle.id}>
                    <TableCell align="center">{cycle.doc || 0}</TableCell>
                    <TableCell align="center">{cycle.startedAt ? new Date(cycle.startedAt).toLocaleDateString() : "-"}</TableCell>
                    <TableCell align="center">{cycle.finishedAt ? new Date(cycle.finishedAt).toLocaleDateString() : "-"}</TableCell>
                    <TableCell align="center">{cycle.totalSeed}</TableCell>
                    <TableCell align="center">{cycle.seedType}</TableCell>
                    <TableCell align="center">{pondDetails?.feedType || "-"}</TableCell>
                    <TableCell align="center">{cycle.fcr ?? "-"}</TableCell>
                    <TableCell align="center">{cycle.adg ?? "-"}</TableCell>
                    <TableCell align="center">{cycle.sr ?? "-"}</TableCell>
                    <TableCell align="center">{cycle.abw ?? "-"}</TableCell>
                    <TableCell align="center">{cycle.harvestWeight ?? "-"}</TableCell>
                    <TableCell align="center" sx={{maxWidth: 100, wordWrap: "break-word"}}>
                      {cycle.notes ?? ""}
                    </TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={() => handleOpenModal(cycle)}>
                        {t("Edit")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Right-side gradient overlay for overflow indication */}
          <Box
            sx={{
              pointerEvents: "none",
              position: "absolute",
              top: 0,
              right: 0,
              width: 32,
              height: "100%",
              background: "linear-gradient(to left, #fff 70%, rgba(255,255,255,0))",
              zIndex: 1,
              display: {xs: "block", md: "none"},
            }}
          />
        </Box>
      )}

      <Dialog open={modalOpen} onClose={!form.isSubmitting ? handleCloseModal : undefined} maxWidth="sm" fullWidth>
        <DialogTitle>{editingCycle ? t("Edit Cycle") : t("Add Cycle")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormField
              name="startedAt"
              label={t("Started At")}
              type="date"
              value={form.values.startedAt}
              onChange={form.handleChange("startedAt")}
              error={form.errors.startedAt}
              required
              disabled={form.isSubmitting}
            />
            <FormField
              name="finishedAt"
              label={t("Finished At")}
              type="date"
              value={form.values.finishedAt}
              onChange={form.handleChange("finishedAt")}
              error={form.errors.finishedAt}
              disabled={form.isSubmitting}
            />
            <FormField
              name="totalSeed"
              label={t("Total Seed")}
              type="number"
              value={form.values.totalSeed}
              onChange={form.handleChange("totalSeed")}
              error={form.errors.totalSeed}
              required
              min={0}
              disabled={form.isSubmitting}
            />
            <FormField
              name="seedType"
              label={t("Seed Type")}
              value={form.values.seedType}
              onSelectChange={form.handleSelectChange("seedType")}
              error={form.errors.seedType}
              select
              required
              options={[
                {value: "hatchery", label: t("Hatchery")},
                {value: "wild", label: t("Wild")},
                {value: "broodstock", label: t("Broodstock")},
                {value: "other", label: t("Other")},
              ]}
              disabled={form.isSubmitting}
            />
            <FormField
              name="feedType"
              label={t("Feed Type")}
              value={form.values.feedType}
              onSelectChange={form.handleSelectChange("feedType")}
              error={form.errors.feedType}
              select
              options={[
                {value: "", label: t("Select Feed Type")},
                ...templateNames.map((template) => ({
                  value: template.templateName,
                  label: template.templateName,
                })),
              ]}
              disabled={form.isSubmitting}
            />
            <FormField
              name="notes"
              label={t("Notes")}
              value={form.values.notes}
              onChange={form.handleChange("notes")}
              error={form.errors.notes}
              multiline
              rows={3}
              disabled={form.isSubmitting}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={form.isSubmitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={form.isSubmitting}>
            {form.isSubmitting ? t("Saving...") : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CycleTab;
